import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Grid,
  IconButton,
  Typography,
  Stack,
  Box,
  Divider,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import axiosClient from "../api/axiosClient";
import { participantService } from "../api/service.ts";
import { useAuth } from "../AuthContext.tsx";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import MapsButton from "./MapsButton.tsx";
import TravelChip from "./TravelChip.tsx";
import HeadToHeadPanel from "./HeadToHeadPanel.tsx";
import ShareSnapshotButton, { SHARE_HIDE } from "./ShareSnapshotButton.tsx";
import { OverallComparison, TeamComparison } from "./SeasonComparison.tsx";
import { useSeason } from "../SeasonContext.tsx";
import type { SeasonComparison } from "../api/types";
import type { TravelInfo } from "../api/types";

type ParticipantDialogProps = {
  open: boolean;
  onClose: () => void;
};

type Player = {
  id: number;
  shirtNumber: number;
  firstName: string;
  lastName: string;
  birthDate: string;
  gender?: string;
};

type Team = {
  teamId: number;
  teamName: string;
  startTime: string;
  entranceTime: string;
  location: string;
  refereeName: string;
  refereePhone: string;
  note: string;
  travel?: TravelInfo | null;
  players: Player[];
};

// Helper function to calculate age from birth date string
const calculateAge = (birthDateStr: string): number => {
  if (!birthDateStr) return 0;
  const birthDate = new Date(birthDateStr);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Helper function to calculate overall tournament statistics
const calculateOverallStats = (teams: Team[]) => {
  if (!teams || teams.length === 0) {
    return {
      totalTeams: 0,
      totalPlayers: 0,
      malePercentage: 0,
      femalePercentage: 0,
      minAge: 0,
      maxAge: 0,
      avgAge: 0,
      ageDistribution: [],
      maleAgeDistribution: [],
      femaleAgeDistribution: [],
      topMaleNames: [],
      topFemaleNames: [],
      topBirthdayDays: [],
      youngestTeams: [],
      mostMatureTeams: [],
    };
  }

  // Collect all players from all teams
  const allPlayers = teams.flatMap(team => team.players || []);
  const totalTeams = teams.length;
  const totalPlayers = allPlayers.length;

  if (totalPlayers === 0) {
    return {
      totalTeams,
      totalPlayers: 0,
      malePercentage: 0,
      femalePercentage: 0,
      minAge: 0,
      maxAge: 0,
      avgAge: 0,
      ageDistribution: [],
      maleAgeDistribution: [],
      femaleAgeDistribution: [],
      topMaleNames: [],
      topFemaleNames: [],
      topBirthdayDays: [],
      youngestTeams: [],
      mostMatureTeams: [],
    };
  }

  // Calculate gender statistics
  const malePlayers = allPlayers.filter(p => p.gender === 'M' || p.gender === 'Maschio' || p.gender === 'm');
  const femalePlayers = allPlayers.filter(p => p.gender === 'F' || p.gender === 'Femmina' || p.gender === 'f');
  const malePercentage = (malePlayers.length / totalPlayers) * 100;
  const femalePercentage = (femalePlayers.length / totalPlayers) * 100;

  // Calculate top 3 most common names with count
  const getTopNames = (players: Player[], count: number = 3): { name: string; count: number }[] => {
    if (players.length === 0) return [];
    const nameCounts: Record<string, number> = {};
    players.forEach(p => {
      const firstName = p.firstName?.trim();
      if (firstName) {
        nameCounts[firstName] = (nameCounts[firstName] || 0) + 1;
      }
    });
    const entries = Object.entries(nameCounts);
    if (entries.length === 0) return [];
    // Sort by count descending, then alphabetically
    entries.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    return entries.slice(0, count).map(([name, cnt]) => ({ name, count: cnt }));
  };

  const topMaleNames = getTopNames(malePlayers);
  const topFemaleNames = getTopNames(femalePlayers);

  // Calculate top 3 birthday days (by day of year, e.g., 25-Apr)
  const getTopBirthdayDays = (players: Player[]): { day: string; count: number }[] => {
    const dayCounts: Record<string, number> = {};
    
    players.forEach(p => {
      if (p.birthDate) {
        const birthDate = new Date(p.birthDate);
        if (!isNaN(birthDate.getTime())) {
          // Format as DD-MMM (e.g., 25-Apr)
          const day = birthDate.getDate();
          const month = birthDate.toLocaleString('it-IT', { month: 'short' });
          const key = `${day.toString().padStart(2, '0')}-${month.charAt(0).toUpperCase() + month.slice(1)}`;
          dayCounts[key] = (dayCounts[key] || 0) + 1;
        }
      }
    });
    
    const entries = Object.entries(dayCounts)
      .map(([day, count]) => ({ day, count }))
      .sort((a, b) => b.count - a.count);
    
    return entries.slice(0, 3);
  };

  const topBirthdayDays = getTopBirthdayDays(allPlayers);

  // Calculate youngest and most mature teams
  const getTeamAgeStats = (teams: Team[]): { youngestTeams: { teamName: string; avgAge: number }[]; mostMatureTeams: { teamName: string; avgAge: number }[] } => {
    if (teams.length === 0) return { youngestTeams: [], mostMatureTeams: [] };
    
    const teamAgeStats = teams.map(team => {
      const ages = team.players?.map(p => calculateAge(p.birthDate)).filter(a => a > 0) || [];
      const avgAge = ages.length > 0 ? ages.reduce((a, b) => a + b, 0) / ages.length : 0;
      return { teamName: team.teamName, avgAge };
    }).filter(t => t.avgAge > 0);
    
    if (teamAgeStats.length === 0) return { youngestTeams: [], mostMatureTeams: [] };
    
    // Sort by average age
    teamAgeStats.sort((a, b) => a.avgAge - b.avgAge);
    
    return {
      youngestTeams: teamAgeStats.slice(0, 3).map(t => ({ teamName: t.teamName, avgAge: Math.round(t.avgAge * 10) / 10 })),
      mostMatureTeams: teamAgeStats.slice(-3).reverse().map(t => ({ teamName: t.teamName, avgAge: Math.round(t.avgAge * 10) / 10 }))
    };
  };

  const teamAgeStats = getTeamAgeStats(teams);

  // Calculate ages
  const ages = allPlayers.map(p => calculateAge(p.birthDate)).filter(a => a > 0);
  const avgAge = ages.reduce((a, b) => a + b, 0) / ages.length;
  const minAge = Math.min(...ages);
  const maxAge = Math.max(...ages);

  // Age distribution ranges - create deep copies for each distribution
  const createRanges = () => [
    { name: "0-9", min: 0, max: 9, count: 0 },
    { name: "10-19", min: 10, max: 19, count: 0 },
    { name: "20-29", min: 20, max: 29, count: 0 },
    { name: "30-39", min: 30, max: 39, count: 0 },
    { name: "40-49", min: 40, max: 49, count: 0 },
    { name: "50-59", min: 50, max: 59, count: 0 },
    { name: "60+", min: 60, max: 150, count: 0 },
  ];

  // Overall age distribution
  const overallRanges = createRanges();
  ages.forEach(age => {
    const range = overallRanges.find(r => age >= r.min && age <= r.max);
    if (range) range.count++;
  });
  const ageDistribution = overallRanges.filter(r => r.count > 0).map(r => ({ name: r.name, value: r.count }));

  // Male age distribution
  const maleAges = malePlayers.map(p => calculateAge(p.birthDate)).filter(a => a > 0);
  const maleRanges = createRanges();
  maleAges.forEach(age => {
    const range = maleRanges.find(r => age >= r.min && age <= r.max);
    if (range) range.count++;
  });
  const maleAgeDistribution = maleRanges.filter(r => r.count > 0).map(r => ({ name: r.name, value: r.count }));

  // Female age distribution
  const femaleAges = femalePlayers.map(p => calculateAge(p.birthDate)).filter(a => a > 0);
  const femaleRanges = createRanges();
  femaleAges.forEach(age => {
    const range = femaleRanges.find(r => age >= r.min && age <= r.max);
    if (range) range.count++;
  });
  const femaleAgeDistribution = femaleRanges.filter(r => r.count > 0).map(r => ({ name: r.name, value: r.count }));

  return {
    totalTeams,
    totalPlayers,
    malePercentage: Math.round(malePercentage * 10) / 10,
    femalePercentage: Math.round(femalePercentage * 10) / 10,
    minAge,
    maxAge,
    avgAge: Math.round(avgAge * 10) / 10,
    ageDistribution,
    maleAgeDistribution,
    femaleAgeDistribution,
    topMaleNames,
    topFemaleNames,
    topBirthdayDays,
    youngestTeams: teamAgeStats.youngestTeams,
    mostMatureTeams: teamAgeStats.mostMatureTeams,
  };
};
const calculateTeamStats = (players: Player[]) => {
  if (!players || players.length === 0) {
    return { avgAge: 0, minAge: 0, maxAge: 0, ageDistribution: [] };
  }

  const ages = players.map(p => calculateAge(p.birthDate));
  const avgAge = ages.reduce((a, b) => a + b, 0) / ages.length;
  const minAge = Math.min(...ages);
  const maxAge = Math.max(...ages);

  // Calculate age distribution in 10-year ranges
  const ranges = [
    { name: "0-9", min: 0, max: 9, count: 0 },
    { name: "10-19", min: 10, max: 19, count: 0 },
    { name: "20-29", min: 20, max: 29, count: 0 },
    { name: "30-39", min: 30, max: 39, count: 0 },
    { name: "40-49", min: 40, max: 49, count: 0 },
    { name: "50-59", min: 50, max: 59, count: 0 },
    { name: "60+", min: 60, max: 150, count: 0 },
  ];

  ages.forEach(age => {
    const range = ranges.find(r => age >= r.min && age <= r.max);
    if (range) range.count++;
  });

  // Filter out empty ranges
  const ageDistribution = ranges.filter(r => r.count > 0).map(r => ({ name: r.name, value: r.count }));

  return { avgAge: Math.round(avgAge * 10) / 10, minAge, maxAge, ageDistribution };
};

export default function ParticipantsDialog({ open, onClose }: ParticipantDialogProps) {
  const { isAdmin } = useAuth();
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const teamCardRef = useRef<HTMLDivElement>(null);
  const { seasons, selectedSeason } = useSeason();
  const [comparison, setComparison] = useState<SeasonComparison | null>(null);
  const [compareTo, setCompareTo] = useState<number | null>(null);
  // stagioni precedenti a quella visualizzata, per il confronto
  const compareOptions = useMemo(() => seasons
    .filter(s => selectedSeason && s.startDate < selectedSeason.startDate)
    .sort((a, b) => b.startDate.localeCompare(a.startDate)), [seasons, selectedSeason]);
  const [error, setError] = useState<string | null>(null);

  // Edit dialog state (info gara squadra)
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    startTime: "",
    entranceTime: "",
    location: "",
    refereeName: "",
    refereePhone: "",
    note: ""
  });
  const [saving, setSaving] = useState(false);

  // Dialog aggiungi/modifica giocatore
  const [playerDialogOpen, setPlayerDialogOpen] = useState(false);
  const [playerDialogMode, setPlayerDialogMode] = useState<"add" | "edit">("add");
  const [playerForm, setPlayerForm] = useState({
    id: null as number | null,
    firstName: "",
    lastName: "",
    birthDate: "",
    shirtNumber: "" as string | number,
    gender: "",
  });
  const [savingPlayer, setSavingPlayer] = useState(false);

  // Dialog conferma eliminazione giocatore
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState<{ id: number; name: string } | null>(null);
  const [deletingPlayer, setDeletingPlayer] = useState(false);

  const selectedTeam = teams.find(t => t.teamId === selectedTeamId) || null;

  // Calculate team statistics using useMemo for optimization
  const teamStats = useMemo(() => {
    if (!selectedTeam?.players || selectedTeam.players.length === 0) {
      return { avgAge: 0, minAge: 0, maxAge: 0, ageDistribution: [] };
    }
    return calculateTeamStats(selectedTeam.players);
  }, [selectedTeam]);

  // Calculate overall tournament statistics
  const overallStats = useMemo(() => {
    return calculateOverallStats(teams);
  }, [teams]);

  // Check if "Overall" is selected (teamId === 0)
  const isOverallSelected = selectedTeamId === 0;

  // Open edit dialog
  const handleOpenEdit = () => {
    if (selectedTeam) {
      setEditForm({
        startTime: selectedTeam.startTime || "",
        entranceTime: selectedTeam.entranceTime || "",
        location: selectedTeam.location || "",
        refereeName: selectedTeam.refereeName || "",
        refereePhone: selectedTeam.refereePhone || "",
        note: selectedTeam.note || ""
      });
      setEditDialogOpen(true);
    }
  };

  // Handle edit form change
  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  // Save team info
  const handleSaveTeam = async () => {
    if (!selectedTeam) return;

    setSaving(true);
    try {
      const response = await axiosClient.put(
        `/participants/team/${selectedTeam.teamId}`,
        editForm
      );

      // Update teams state with the returned data
      setTeams(prevTeams => prevTeams.map(team =>
        team.teamId === selectedTeam.teamId ? response.data : team
      ));

      setEditDialogOpen(false);
    } catch (err) {
      console.error("Failed to update team:", err);
      setError("Failed to update team info");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAddPlayer = () => {
    setPlayerForm({ id: null, firstName: "", lastName: "", birthDate: "", shirtNumber: "", gender: "" });
    setPlayerDialogMode("add");
    setPlayerDialogOpen(true);
  };

  const handleOpenEditPlayer = (player: Player) => {
    setPlayerForm({
      id: player.id,
      firstName: player.firstName,
      lastName: player.lastName,
      birthDate: player.birthDate,
      shirtNumber: player.shirtNumber,
      gender: player.gender || "",
    });
    setPlayerDialogMode("edit");
    setPlayerDialogOpen(true);
  };

  const handleSavePlayer = async () => {
    if (!selectedTeam) return;
    setSavingPlayer(true);
    try {
      const payload = {
        firstName: playerForm.firstName,
        lastName: playerForm.lastName,
        birthDate: playerForm.birthDate,
        shirtNumber: playerForm.shirtNumber !== "" ? Number(playerForm.shirtNumber) : null,
        gender: playerForm.gender,
      };
      if (playerDialogMode === "add") {
        const newPlayer = await participantService.addPlayer(selectedTeam.teamId, payload);
        setTeams(prev => prev.map(t =>
          t.teamId === selectedTeam.teamId ? { ...t, players: [...t.players, newPlayer] } : t
        ));
      } else {
        const updated = await participantService.updatePlayer(playerForm.id!, payload);
        setTeams(prev => prev.map(t =>
          t.teamId === selectedTeam.teamId
            ? { ...t, players: t.players.map(p => p.id === updated.id ? updated : p) }
            : t
        ));
      }
      setPlayerDialogOpen(false);
    } catch (err) {
      console.error("Failed to save player:", err);
      setError("Errore nel salvataggio del giocatore");
    } finally {
      setSavingPlayer(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!playerToDelete || !selectedTeam) return;
    setDeletingPlayer(true);
    try {
      await participantService.deletePlayer(playerToDelete.id);
      setTeams(prev => prev.map(t =>
        t.teamId === selectedTeam.teamId
          ? { ...t, players: t.players.filter(p => p.id !== playerToDelete.id) }
          : t
      ));
      setDeleteConfirmOpen(false);
      setPlayerToDelete(null);
    } catch (err) {
      console.error("Failed to delete player:", err);
      setError("Errore nell'eliminazione del giocatore");
    } finally {
      setDeletingPlayer(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    participantService.getSeasonComparison(compareTo).then(setComparison).catch(() => setComparison(null));
  }, [open, compareTo]);

  useEffect(() => {
    if (open) {
      setSelectedTeamId(0);   // all'apertura: statistiche complessive del torneo
      setLoading(true);
      setError(null);
      axiosClient
        .get("/participants/getAll")
        .then((res) => setTeams(res.data))
        .catch((err) => {
          console.error(err);
          setError("Failed to load data");
        })
        .finally(() => setLoading(false));
    }
  }, [open]);

  return (
    <Dialog fullScreen open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center",backgroundColor: "primary.main" }} >
          Partecipanti
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p style={{ color: "red" }}>{error}</p>
        ) : (
          <Grid container spacing={1}>

            {/* LEFT COLUMN */}
            <Grid
              size={{ xs: 3 }}
              sx={{
                borderRight: "1px solid #ddd",
                height: "calc(100vh - 64px)",
                overflowY: "auto",
                background: "#fafafa",
                pr: 1,
              }}
            >
              <List dense>
                {/* Overall Tournament Statistics */}
                <ListItem disablePadding>
                  <ListItemButton
                    selected={selectedTeamId === 0}
                    onClick={() => setSelectedTeamId(0)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      backgroundColor: selectedTeamId === 0 ? "#e8f5e9" : "transparent",
                      border: selectedTeamId === 0 ? "2px solid #4caf50" : "2px solid transparent",
                      "&:hover": { backgroundColor: selectedTeamId === 0 ? "#c8e6c9" : "#f5f5f5" }
                    }}
                  >
                    <ListItemText
                      primary="📊 Overall"
                      primaryTypographyProps={{ style: { fontSize: "0.8rem", fontWeight: "bold" } }}
                    />
                  </ListItemButton>
                </ListItem>
                <Divider sx={{ my: 1 }} />
                {teams.map((team) => (
                  <ListItem disablePadding key={team.teamId}>
                    <ListItemButton
                      selected={selectedTeamId === team.teamId}
                      onClick={() => setSelectedTeamId(team.teamId)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.1,
                        "&.Mui-selected": {
                          background: "#1976d2",
                          color: "white",
                          "&:hover": { background: "#1565c0" }
                        }
                      }}
                    >
                      <ListItemText
                        primary={team.teamName}
                        primaryTypographyProps={{ style: { fontSize: "0.75rem" } }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Grid>

            {/* RIGHT COLUMN */}
            <Grid size={{ xs: 9 }}>
              {isOverallSelected ? (
                <Card sx={{ height: "calc(100vh - 64px)", overflowY: "auto" }}>
                  <CardContent sx={{ p: 2 }}>
                    {/* Overall Statistics Header */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="h6" sx={{ color: "#4caf50", fontWeight: "bold" }}>
                        📊 Statistiche Complessive Torneo
                      </Typography>
                    </Box>

                    {comparison && (
                      <OverallComparison data={comparison} options={compareOptions} compareTo={compareTo}
                        onCompareToChange={setCompareTo} />
                    )}

                    {/* Main Stats Grid */}
                    <Box sx={{ mb: 3, p: 2, backgroundColor: "#e8f5e9", borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2, color: "#2e7d32" }}>
                        Riepilogo Tournament
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 4 }}>
                          <Box sx={{ p: 1.5, backgroundColor: "white", borderRadius: 1, textAlign: "center" }}>
                            <Typography variant="h4" sx={{ color: "#1976d2", fontWeight: "bold" }}>
                              {overallStats.totalTeams}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Squadre Partecipanti
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                          <Box sx={{ p: 1.5, backgroundColor: "white", borderRadius: 1, textAlign: "center" }}>
                            <Typography variant="h4" sx={{ color: "#7b1fa2", fontWeight: "bold" }}>
                              {overallStats.totalPlayers}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Giocatori Totali
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                          <Box sx={{ p: 1.5, backgroundColor: "white", borderRadius: 1, textAlign: "center" }}>
                            <Typography variant="h4" sx={{ color: "#388e3c", fontWeight: "bold" }}>
                              {overallStats.avgAge}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Età Media
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Gender Distribution */}
                    <Box sx={{ mb: 3, p: 2, backgroundColor: "#fce4ec", borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2, color: "#c2185b" }}>
                        Distribuzione per Genere
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                          <Box sx={{ p: 1.5, backgroundColor: "white", borderRadius: 1, textAlign: "center" }}>
                            <Typography variant="h5" sx={{ color: "#1976d2", fontWeight: "bold" }}>
                              {overallStats.malePercentage}% ({Math.round(overallStats.totalPlayers * overallStats.malePercentage / 100)})
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Maschi ♂
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Box sx={{ p: 1.5, backgroundColor: "white", borderRadius: 1, textAlign: "center" }}>
                            <Typography variant="h5" sx={{ color: "#e91e63", fontWeight: "bold" }}>
                              {overallStats.femalePercentage}% ({Math.round(overallStats.totalPlayers * overallStats.femalePercentage / 100)})
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Femmine ♀
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Age Range */}
                    <Box sx={{ mb: 3, p: 2, backgroundColor: "#fff3e0", borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2, color: "#e65100" }}>
                        Età Minima e Massima
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                          <Box sx={{ p: 1.5, backgroundColor: "white", borderRadius: 1, textAlign: "center" }}>
                            <Typography variant="h5" sx={{ color: "#4caf50", fontWeight: "bold" }}>
                              {overallStats.minAge} anni
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Più Giovane
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Box sx={{ p: 1.5, backgroundColor: "white", borderRadius: 1, textAlign: "center" }}>
                            <Typography variant="h5" sx={{ color: "#f57c00", fontWeight: "bold" }}>
                              {overallStats.maxAge} anni
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Più Anziano
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Most Common Names and Birthday Days */}
                    <Box sx={{ mb: 3, p: 2, backgroundColor: "#e0f7fa", borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2, color: "#00695c" }}>
                        Nomi più Comuni e Giorni di Compleanno
                      </Typography>
                      <Grid container spacing={2}>
                        {/* Top 3 Male Names */}
                        <Grid size={{ xs: 6 }}>
                          <Box sx={{ p: 1.5, backgroundColor: "white", borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1, color: "#1976d2" }}>
                              Top 3 Nomi Maschili ♂
                            </Typography>
                            {overallStats.topMaleNames.length > 0 ? (
                              overallStats.topMaleNames.map((item, index) => (
                                <Box key={index} sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                  <Typography variant="body2">
                                    {index + 1}. {item.name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    ({item.count})
                                  </Typography>
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </Box>
                        </Grid>
                        {/* Top 3 Female Names */}
                        <Grid size={{ xs: 6 }}>
                          <Box sx={{ p: 1.5, backgroundColor: "white", borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1, color: "#e91e63" }}>
                              Top 3 Nomi Femminili ♀
                            </Typography>
                            {overallStats.topFemaleNames.length > 0 ? (
                              overallStats.topFemaleNames.map((item, index) => (
                                <Box key={index} sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                  <Typography variant="body2">
                                    {index + 1}. {item.name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    ({item.count})
                                  </Typography>
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                      {/* Top Birthday Days */}
                      {overallStats.topBirthdayDays.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1, color: "#00695c" }}>
                            Giorni con più Compleanni 🎂
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {overallStats.topBirthdayDays.map((item, index) => (
                              <Box 
                                key={index}
                                sx={{ 
                                  px: 2, 
                                  py: 1, 
                                  backgroundColor: index === 0 ? "#ffeb3b" : index === 1 ? "#ff9800" : "#4caf50",
                                  borderRadius: 1,
                                  color: index <= 1 ? "white" : "white",
                                  fontWeight: "bold",
                                  textAlign: "center"
                                }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: "bold", color: index <= 1 ? "white" : "white" }}>
                                  {item.day}
                                </Typography>
                                <Typography variant="caption" sx={{ color: index <= 1 ? "white" : "white" }}>
                                  {item.count} compleanni
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>

                    {/* Youngest and Most Mature Teams */}
                    <Box sx={{ mb: 3, p: 2, backgroundColor: "#fff8e1", borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2, color: "#f57c00" }}>
                        Classifica Squadre per Età
                      </Typography>
                      <Grid container spacing={2}>
                        {/* Top 3 Youngest Teams */}
                        <Grid size={{ xs: 6 }}>
                          <Box sx={{ p: 1.5, backgroundColor: "white", borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1, color: "#4caf50" }}>
                              🏆 Prime 3 Squadre più Giovani
                            </Typography>
                            {overallStats.youngestTeams.length > 0 ? (
                              overallStats.youngestTeams.map((item, index) => (
                                <Box key={index} sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                  <Typography variant="body2">
                                    {index + 1}. {item.teamName}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {item.avgAge} anni
                                  </Typography>
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </Box>
                        </Grid>
                        {/* Top 3 Most Mature Teams */}
                        <Grid size={{ xs: 6 }}>
                          <Box sx={{ p: 1.5, backgroundColor: "white", borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1, color: "#f57c00" }}>
                              👴 Prime 3 Squadre più "Mature"
                            </Typography>
                            {overallStats.mostMatureTeams.length > 0 ? (
                              overallStats.mostMatureTeams.map((item, index) => (
                                <Box key={index} sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                  <Typography variant="body2">
                                    {index + 1}. {item.teamName}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {item.avgAge} anni
                                  </Typography>
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Overall Age Distribution */}
                    {overallStats.ageDistribution.length > 0 && (
                      <Box sx={{ mb: 3, p: 2, backgroundColor: "#e3f2fd", borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1, color: "#1565c0" }}>
                          Distribuzione Età Complessiva
                        </Typography>
                        <Box sx={{ height: 250 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={overallStats.ageDistribution} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis allowDecimals={false} />
                              <Tooltip />
                              <Bar dataKey="value" fill="#0088FE">
                                {overallStats.ageDistribution.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={[
                                    "#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d", "#ffc0cb"
                                  ][index % 7]} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Box>
                    )}

                    {/* Gender-specific Age Distributions */}
                    {(overallStats.maleAgeDistribution.length > 0 || overallStats.femaleAgeDistribution.length > 0) && (
                      <Box sx={{ mb: 3, p: 2, backgroundColor: "#f3e5f5", borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2, color: "#6a1b9a" }}>
                          Distribuzione Età per Genere
                        </Typography>
                        <Grid container spacing={2}>
                          {/* Male Age Distribution */}
                          {overallStats.maleAgeDistribution.length > 0 && (
                            <Grid size={{ xs: 6 }}>
                              <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1, color: "#1976d2" }}>
                                Maschi ♂
                              </Typography>
                              <Box sx={{ height: 200 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart data={overallStats.maleAgeDistribution} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#1976d2">
                                      {overallStats.maleAgeDistribution.map((entry, index) => (
                                        <Cell key={`male-${index}`} fill={[
                                          "#1976d2", "#42a5f5", "#64b5f6", "#90caf9", "#bbdefb", "#e3f2fd", "#e1f5fe"
                                        ][index % 7]} />
                                      ))}
                                    </Bar>
                                  </BarChart>
                                </ResponsiveContainer>
                              </Box>
                            </Grid>
                          )}
                          {/* Female Age Distribution */}
                          {overallStats.femaleAgeDistribution.length > 0 && (
                            <Grid size={{ xs: 6 }}>
                              <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1, color: "#e91e63" }}>
                                Femmine ♀
                              </Typography>
                              <Box sx={{ height: 200 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart data={overallStats.femaleAgeDistribution} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#e91e63">
                                      {overallStats.femaleAgeDistribution.map((entry, index) => (
                                        <Cell key={`female-${index}`} fill={[
                                          "#e91e63", "#f06292", "#f48fb1", "#f8bbd0", "#fce4ec", "#fce4ec", "#fce4ec"
                                        ][index % 7]} />
                                      ))}
                                    </Bar>
                                  </BarChart>
                                </ResponsiveContainer>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ) : selectedTeam ? (
                <Card sx={{ height: "calc(100vh - 64px)", overflowY: "auto" }}>
                  <CardContent sx={{ p: 2 }} ref={teamCardRef}>
                    {/* Team Info Section */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="h6" sx={{ color: "primary.main" }}>
                        {selectedTeam.teamName}
                      </Typography>
                      <Stack direction="row" gap={1}>
                        <ShareSnapshotButton targetRef={teamCardRef} title={selectedTeam.teamName}
                          fileName={`Scheda ${selectedTeam.teamName}`.replace(/[^\w\- ]+/g, "")} />
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={handleOpenEdit}
                          {...SHARE_HIDE}
                        >
                          Modifica
                        </Button>
                      </Stack>
                    </Box>
                    
                    <Box sx={{ mb: 3, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                        Informazioni Gara
                      </Typography>
                      <Grid container spacing={2}>
                        {selectedTeam.startTime && (
                          <Grid size={{ xs: 6 }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Orario Inizio:</strong> {selectedTeam.startTime}
                            </Typography>
                          </Grid>
                        )}
                        {selectedTeam.entranceTime && (
                          <Grid size={{ xs: 6 }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Orario Ingresso:</strong> {selectedTeam.entranceTime}
                            </Typography>
                          </Grid>
                        )}
                        {selectedTeam.location && (
                          <Grid size={{ xs: 6 }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Luogo:</strong> {selectedTeam.location}
                              <span {...SHARE_HIDE}><MapsButton address={selectedTeam.location} variant="icon" /></span>
                            </Typography>
                            <TravelChip travel={selectedTeam.travel} />
                          </Grid>
                        )}
                        {selectedTeam.refereeName && (
                          <Grid size={{ xs: 6 }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Referente:</strong> {selectedTeam.refereeName}
                            </Typography>
                          </Grid>
                        )}
                        {selectedTeam.refereePhone && (
                          <Grid size={{ xs: 6 }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Telefono Referente:</strong> {selectedTeam.refereePhone}
                            </Typography>
                          </Grid>
                        )}
                        {selectedTeam.note && (
                          <Grid size={{ xs: 12 }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Note:</strong> {selectedTeam.note}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>

                    {selectedTeam.teamName !== "VB Volley" && (
                      <Box sx={{ mb: 3 }}>
                        <HeadToHeadPanel teamId={selectedTeam.teamId} />
                      </Box>
                    )}

                    <TeamComparison previousSeason={comparison?.previousSeason ?? null}
                      diff={comparison?.teams.find(t => t.teamId === selectedTeam.teamId)} />

                    {/* Statistics Section */}
                    {selectedTeam.players && selectedTeam.players.length > 0 && (
                      <Box sx={{ mb: 3, p: 2, backgroundColor: "#e3f2fd", borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                          Statistiche Squadra
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Età Media:</strong> {teamStats.avgAge} anni
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Età Minima:</strong> {teamStats.minAge} anni
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Età Massima:</strong> {teamStats.maxAge} anni
                            </Typography>
                          </Grid>
                        </Grid>
                        
                        {/* Age Distribution Bar Chart */}
                        {teamStats.ageDistribution.length > 0 && (
                          <Box sx={{ mt: 2, height: 250 }}>
                            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                              Distribuzione Età
                            </Typography>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={teamStats.ageDistribution} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="value" fill="#0088FE">
                                  {teamStats.ageDistribution.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={[
                                      "#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d", "#ffc0cb"
                                    ][index % 7]} />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        )}
                      </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* Players Table */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                        Giocatori ({selectedTeam.players?.length || 0})
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddPlayer}
                        {...SHARE_HIDE}
                      >
                        Aggiungi
                      </Button>
                    </Box>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
                      <thead>
                        <tr style={{ textAlign:"left", backgroundColor: "#ececec" }}>
                          <th style={{ padding: 4 }}>#</th>
                          <th style={{ padding: 4 }}>Nome</th>
                          <th style={{ padding: 4 }}>Cognome</th>
                          <th style={{ padding: 4 }}>Data di Nascita</th>
                          <th style={{ padding: 4 }}>Età</th>
                          <th style={{ padding: 4 }}>Genere</th>
                          <th style={{ padding: 4 }} {...SHARE_HIDE}>Azioni</th>
                        </tr>
                      </thead>

                      <tbody>
                        {selectedTeam.players?.map((p, idx) => (
                          <tr
                            key={p.id}
                            style={{
                              backgroundColor: idx % 2 === 0 ? "#f7f7f7" : "#fff",
                              transition: "0.2s",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e0e0e0")}
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.backgroundColor = idx % 2 === 0 ? "#f7f7f7" : "#fff")
                            }
                          >
                            <td style={{ padding: 2 }}>{p.shirtNumber}</td>
                            <td style={{ padding: 2 }}>{p.firstName}</td>
                            <td style={{ padding: 2 }}>{p.lastName}</td>
                            <td style={{ padding: 2 }}>{p.birthDate}</td>
                            <td style={{ padding: 2 }}>{calculateAge(p.birthDate)}</td>
                            <td style={{ padding: 2 }}>{p.gender}</td>
                            <td style={{ padding: 2 }} {...SHARE_HIDE}>
                              <IconButton size="small" onClick={() => handleOpenEditPlayer(p)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              {isAdmin && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setPlayerToDelete({ id: p.id, name: `${p.firstName} ${p.lastName}` });
                                    setDeleteConfirmOpen(true);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              ) : (
                <p style={{ color: "#666" }}>Seleziona una squadra dalla lista</p>
              )}
            </Grid>

          </Grid>
        )}
      </DialogContent>

      {/* Dialog aggiungi/modifica giocatore */}
      <Dialog open={playerDialogOpen} onClose={() => setPlayerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {playerDialogMode === "add" ? "Aggiungi Giocatore" : "Modifica Giocatore"}
          <IconButton onClick={() => setPlayerDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Nome"
                value={playerForm.firstName}
                onChange={e => setPlayerForm(prev => ({ ...prev, firstName: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Cognome"
                value={playerForm.lastName}
                onChange={e => setPlayerForm(prev => ({ ...prev, lastName: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Data di Nascita"
                type="date"
                value={playerForm.birthDate}
                onChange={e => setPlayerForm(prev => ({ ...prev, birthDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <TextField
                fullWidth
                label="N. Maglia"
                type="number"
                value={playerForm.shirtNumber}
                onChange={e => setPlayerForm(prev => ({ ...prev, shirtNumber: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Genere</InputLabel>
                <Select
                  value={playerForm.gender}
                  label="Genere"
                  onChange={e => setPlayerForm(prev => ({ ...prev, gender: e.target.value }))}
                >
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="F">F</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 1 }}>
            <Button variant="outlined" onClick={() => setPlayerDialogOpen(false)}>
              Annulla
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSavePlayer}
              disabled={savingPlayer}
            >
              {savingPlayer ? "Salvataggio..." : "Salva"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione giocatore */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare <strong>{playerToDelete?.name}</strong>?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 1 }}>
            <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)}>
              Annulla
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              disabled={deletingPlayer}
            >
              {deletingPlayer ? "Eliminazione..." : "Elimina"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Edit Team Info Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Modifica Informazioni Gara
          <IconButton onClick={() => setEditDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Orario Inizio Gara"
                name="startTime"
                value={editForm.startTime}
                onChange={handleEditFormChange}
                placeholder="HH:mm"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Orario Ingresso Palestra"
                name="entranceTime"
                value={editForm.entranceTime}
                onChange={handleEditFormChange}
                placeholder="HH:mm"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Luogo"
                name="location"
                value={editForm.location}
                onChange={handleEditFormChange}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Nome Referente"
                name="refereeName"
                value={editForm.refereeName}
                onChange={handleEditFormChange}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Telefono Referente"
                name="refereePhone"
                value={editForm.refereePhone}
                onChange={handleEditFormChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Note"
                name="note"
                value={editForm.note}
                onChange={handleEditFormChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setEditDialogOpen(false)}
            >
              Annulla
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveTeam}
              disabled={saving}
            >
              {saving ? "Salvataggio..." : "Salva"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
